/**
 * Direct Event Emitter Hook
 * 
 * Provides convenience methods for emitting canonical system events
 * at the moment of action (wrap submission, issue creation, QA, payment).
 * Eliminates reliance on state-diffing for critical actions.
 */

import { useCallback } from "react";
import { useAutomationRunner } from "@/data/AutomationRunnerContext";
import { createSystemEvent } from "@/data/eventFactory";
import type { Job } from "@/data/types";

export function useEventEmitter() {
  const { emitEvent } = useAutomationRunner();

  const emitWrapSubmitted = useCallback((job: Job, outcome: string, completionPercent: number) => {
    const event = createSystemEvent({
      type: "wrap_submitted",
      jobId: job.id,
      payload: {
        entityName: job.customerName,
        outcome,
        completionPercent,
      },
      triggeredBy: job.assignedCrew || "Installer",
    });
    emitEvent(event, job);
  }, [emitEvent]);

  const emitIssueCreated = useCallback((job: Job, issueType: string, severity: string) => {
    const event = createSystemEvent({
      type: "issue_created",
      jobId: job.id,
      payload: {
        entityName: job.customerName,
        issueTitle: issueType,
        severity,
      },
      triggeredBy: "System",
    });
    emitEvent(event, job);
  }, [emitEvent]);

  const emitQACompleted = useCallback((job: Job, passed: boolean) => {
    const event = createSystemEvent({
      type: passed ? "qa_passed" : "qa_submitted",
      jobId: job.id,
      payload: {
        entityName: job.customerName,
        qaResult: passed ? "Passed" : "Submitted",
      },
      triggeredBy: "QA Inspector",
    });
    emitEvent(event, job);
  }, [emitEvent]);

  const emitPaymentCollected = useCallback((job: Job, amount: number, method: string) => {
    const event = createSystemEvent({
      type: "payment_collected",
      jobId: job.id,
      payload: {
        entityName: job.customerName,
        amount,
        method,
      },
      triggeredBy: "System",
    });
    emitEvent(event, job);
  }, [emitEvent]);

  const emitInstallCompleted = useCallback((job: Job) => {
    const event = createSystemEvent({
      type: "install_completed",
      jobId: job.id,
      payload: {
        entityName: job.customerName,
      },
      triggeredBy: job.assignedCrew || "Installer",
    });
    emitEvent(event, job);
  }, [emitEvent]);

  const emitAdminOverride = useCallback((job: Job, action: string, reason: string) => {
    const event = createSystemEvent({
      type: "admin_override",
      jobId: job.id,
      payload: {
        entityName: job.customerName,
        action,
        reason,
      },
      triggeredBy: "Admin",
    });
    emitEvent(event, job);
  }, [emitEvent]);

  return {
    emitWrapSubmitted,
    emitIssueCreated,
    emitQACompleted,
    emitPaymentCollected,
    emitInstallCompleted,
    emitAdminOverride,
  };
}
